import { useState, useMemo, useEffect } from 'react';
import ProductsHeroFr from './components/ProductsHeroFr';
import ProductCardFr from './components/ProductCardFr';
import ProductModalFr from './components/ProductModalFr';
import { products, productCategories } from '@/mocks/products';
import { categoryTranslationsFr } from '@/mocks/productsFr';

type Product = typeof products[0];

const PRODUCTS_SCHEMA_FR = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/fr/produits#webpage`,
  url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/fr/produits`,
  name: 'Produits | MAX-Sourcing Produits Chimiques Fins & IPA Pharmaceutiques',
  description:
    'Parcourez le catalogue MAX-Sourcing d\'IPA pharmaceutiques, produits chimiques fins, ingrédients alimentaires, réactifs industriels et accès à la fabrication de comprimés effervescents conformes aux BPF.',
  inLanguage: 'fr',
  isPartOf: {
    '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/#website`,
  },
  dateModified: '2026-04-07',
  translationOfWork: {
    '@type': 'CollectionPage',
    '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/products#webpage`,
    url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/products`,
    inLanguage: 'en',
  },
  workTranslation: {
    '@type': 'CollectionPage',
    '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/products#webpage`,
    url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/products`,
    inLanguage: 'en',
  },
};

export default function ProductsPageFr() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  useEffect(() => {
    document.title = 'Produits | MAX-Sourcing Produits Chimiques Fins & IPA Pharmaceutiques';
    const id = 'schema-products-fr';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(PRODUCTS_SCHEMA_FR);
    return () => { document.getElementById(id)?.remove(); };
  }, []);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchCat = activeCategory === 'All' || p.category === activeCategory;
      const matchSearch =
        search.trim() === '' ||
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.grade.toLowerCase().includes(search.toLowerCase()) ||
        p.category.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [activeCategory, search]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <main className="flex-1">
        <ProductsHeroFr />
        <div className="w-full bg-white border-b border-stone-100 sticky top-20 z-30">
          <div className="w-full px-6 md:px-16 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative w-full sm:w-72 shrink-0">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Rechercher des produits..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {productCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                    activeCategory === cat ? 'bg-emerald-700 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {categoryTranslationsFr[cat] ?? cat}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="w-full px-6 md:px-16 py-12">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <i className="ri-search-line text-4xl mb-3 block" />
              <p className="text-base font-medium">Aucun produit trouvé</p>
              <p className="text-sm mt-1">Essayez d&apos;ajuster votre recherche ou filtre de catégorie</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-stone-400 font-semibold uppercase tracking-widest mb-6">
                {filtered.length} produit{filtered.length !== 1 ? 's' : ''} trouvé{filtered.length !== 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filtered.map((product) => (
                  <ProductCardFr key={product.id} product={product} onSelect={setSelectedProduct} />
                ))}
              </div>
            </>
          )}
        </div>
        <div className="w-full bg-emerald-700 px-6 md:px-16 py-14 text-center">
          <h2 className="text-3xl font-extrabold text-white mb-3" style={{ fontFamily: "'DM Serif Display', serif" }}>
            Vous ne trouvez pas ce dont vous avez besoin ?
          </h2>
          <p className="text-emerald-100 text-base mb-7 max-w-xl mx-auto">
            Nous avons accès à une large gamme de produits, d&apos;ingrédients et de produits chimiques fins au-delà de cette liste. Contactez-nous avec vos exigences.
          </p>
          <a href="/fr/contact" className="inline-block bg-white text-emerald-700 font-bold text-sm px-8 py-3 rounded-md hover:bg-emerald-50 transition-colors whitespace-nowrap cursor-pointer">
            Envoyer une demande
          </a>
        </div>
      </main>
      {selectedProduct && <ProductModalFr product={selectedProduct} onClose={() => setSelectedProduct(null)} />}
    </div>
  );
}
